import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useEffect, useRef } from "react";
import { FONT_SIZE, ICON_SIZE, INK, SHADOW } from "../../../theme/tokens";
import { ChatCard } from "./ChatCard";

type Props = {
	/** Already-translated headline, e.g. "Show me how to read the Day Schedule". */
	title: string;
	/** Already-translated supporting line, e.g. "Guided tour · 3 steps". */
	meta: string;
	/** Accessible name — say what will happen, since activating this moves the user. */
	ariaLabel: string;
	onStart: () => void;
	/** Called once, when the offer first reaches the screen — the denominator for its accept rate. */
	onShown?: () => void;
	dataTestId?: string;
};

/** The circular brand badge holding the start glyph — the card's one focal moment. */
const BADGE_SIZE = 36;

/**
 * The call to action under a chat answer for being shown that answer in the live UI.
 *
 * Deliberately NOT a chip. It used to render the same `MetaChip tone="primary" size="small"` as
 * `ChatSuggestions`, which made the one control that navigates the app and starts an overlay tour
 * indistinguishable from a "ask me about this next" chip — and put a full sentence in a `nowrap`
 * caption, so the destination was the first thing to ellipsize on a narrow panel.
 *
 * So it is an interactive `ChatCard` instead: a start badge, the tour's own headline (previously carried
 * only as an aria-label and never shown), and what the user is committing to on a second line. One
 * control, one action — the whole card is the target.
 */
export const ChatGuidedOffer: FC<Props> = ({ title, meta, ariaLabel, onStart, onShown, dataTestId }) => {
	// Once per mounted offer: the transcript re-renders on every streamed token and `onShown` is an inline
	// arrow, so an unguarded effect would report one offer hundreds of times.
	const hasReportedRef = useRef(false);
	useEffect(() => {
		if (hasReportedRef.current || onShown === undefined) return;
		hasReportedRef.current = true;
		onShown();
	}, [onShown]);

	return (
		<ChatCard
			dataTestId={dataTestId}
			onClick={onStart}
			ariaLabel={ariaLabel}
			sx={(theme) => ({
				mt: 2,
				flexDirection: "row",
				alignItems: "center",
				gap: 1.5,
				p: 1.5,
				borderColor: alpha(theme.palette.primary.main, 0.32),
				// A brand wash rather than a flat tint: the offer has to out-weigh the suggestion chips
				// directly above it without becoming a second answer.
				backgroundImage: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.07)} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
			})}
		>
			<Box
				aria-hidden
				sx={(theme) => ({
					flex: "none",
					width: BADGE_SIZE,
					height: BADGE_SIZE,
					borderRadius: "50%",
					display: "grid",
					placeItems: "center",
					backgroundColor: theme.palette.primary.main,
					color: theme.palette.primary.contrastText,
					boxShadow: SHADOW.CONTROL_RAISED,
				})}
			>
				<PlayArrowRoundedIcon sx={{ fontSize: ICON_SIZE.CONTROL }} />
			</Box>
			<Box sx={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", gap: 0.25 }}>
				<Typography component="span" sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, lineHeight: 1.3, color: INK.PRIMARY }}>
					{title}
				</Typography>
				<Typography component="span" sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>
					{meta}
				</Typography>
			</Box>
			<ChevronRightRoundedIcon aria-hidden sx={(theme) => ({ flex: "none", fontSize: ICON_SIZE.CONTROL, color: theme.palette.primary.main })} />
		</ChatCard>
	);
};
